import React from 'react';
import { motion } from 'framer-motion';
import { services } from '../data/mock';
import { Package, PenTool, Box, RefreshCw, ClipboardCheck, LifeBuoy, ArrowUpRight, FlaskConical, Thermometer, GraduationCap, CheckCircle2 } from 'lucide-react';

const iconMap = { Package, PenTool, Box, RefreshCw, ClipboardCheck, LifeBuoy, FlaskConical, Thermometer, GraduationCap };

const ServicesSection = () => {
  return (
    <section id="services" className="py-24 md:py-32 bg-[#f5f5f7]">
      <div className="max-w-6xl mx-auto px-5 md:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="badge-minimal mb-6">
            Our Services
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1a1a2e] mb-6">
            Comprehensive <span className="bg-gradient-to-r from-[#1C4B8A] to-[#2A5F9E] bg-clip-text text-transparent">Cleanroom</span> Solutions
          </h2>
          <p className="text-lg text-[#4a4a5a] max-w-2xl mx-auto">
            From initial concept to final certification, we deliver comprehensive cleanroom 
            services tailored to your industry requirements.
          </p>
        </motion.div>

        {/* Glass Cards Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => {
            const IconComponent = iconMap[service.icon] || Package;
            return (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
                className="group p-7 rounded-3xl transition-all duration-500 hover:-translate-y-2 cursor-pointer"
                style={{
                  background: 'rgba(255, 255, 255, 0.75)',
                  backdropFilter: 'blur(30px)',
                  WebkitBackdropFilter: 'blur(30px)',
                  border: '1px solid rgba(255, 255, 255, 0.8)',
                  boxShadow: '0 4px 24px rgba(0, 0, 0, 0.06)'
                }}
              >
                <div className="flex items-start justify-between mb-5">
                  <div 
                    className="w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-500 group-hover:scale-110"
                    style={{
                      background: 'linear-gradient(135deg, #1C4B8A 0%, #153A6B 100%)',
                      boxShadow: '0 4px 16px rgba(28, 75, 138, 0.3)'
                    }}
                  >
                    <IconComponent className="w-7 h-7 text-white" />
                  </div>
                  <ArrowUpRight className="w-5 h-5 text-[#86868b] opacity-0 group-hover:opacity-100 transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </div>
                
                <h3 className="text-lg font-bold text-[#1a1a2e] mb-3 group-hover:text-[#1C4B8A] transition-colors">
                  {service.title}
                </h3>
                <p className="text-[#4a4a5a] mb-5 leading-relaxed text-sm">
                  {service.description}
                </p>

                {service.features && (
                  <ul className="space-y-2.5">
                    {service.features.slice(0, 3).map((feature, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-sm text-[#3c3c43]">
                        <CheckCircle2 className="w-4 h-4 text-[#1C4B8A] mt-0.5 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
